import ConfirmationNumberOutlined from '@mui/icons-material/ConfirmationNumberOutlined';
import PeopleAltOutlined from '@mui/icons-material/PeopleAltOutlined';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import { Chip } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, unwrapJson } from '../../../api/client';
import { EmptyState } from '../../../components/states';
import { moneyCompact, qty as fmtQty } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { ReportFrame } from '../components/ReportFrame';
import { ReportTable } from '../components/ReportTable';
import { RankingBars, TrendChart } from '../components/charts';
import { Panel, QueryError, Segmented, StatGrid, StatTile } from '../components/ui';
import { useReportParams, usePeriodText } from '../components/useReportParams';
import { fetchCsv, reportFileName } from '../lib/csv';
import { periodLabel } from '../lib/dateRange';
import { num, share } from '../lib/numbers';
import { ItemCell, M, Q, RangeControls } from './common';
import { PartyDocumentRows } from './PartyDocuments';

const emptyState = (
  <EmptyState
    icon={<ReceiptLongRounded />}
    title="No sales in this period"
    body="Try a longer date range, or record a sale to see it here."
  />
);

export function SalesByPeriodReport() {
  const p = useReportParams('30d');
  const { format } = useShop();
  const query = { ...p.range, interval: p.interval };
  const q = useQuery({
    queryKey: ['reports', 'sales-by-period', query],
    queryFn: () => unwrapJson(api.GET('/reports/sales/by-period', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const rows = q.data?.data;
  const t = q.data?.totals;
  const best = rows?.reduce<(typeof rows)[number] | undefined>(
    (b, r) => (!b || num(r.total) > num(b.total) ? r : b),
    undefined,
  );
  return (
    <ReportFrame
      slug="sales-by-period"
      period={usePeriodText(p)}
      controls={<RangeControls p={p} interval />}
      onExport={() =>
        fetchCsv(
          api.GET('/reports/sales/by-period', {
            params: { query: { ...query, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName(`sales-by-${p.interval}`, p.range),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Total sales"
          value={t && moneyCompact(t.total, format)}
          hint={t && `Net ${moneyCompact(t.net, format)} · tax ${moneyCompact(t.tax, format)}`}
          icon={<TrendingUpRounded />}
          loading={q.isLoading}
        />
        <StatTile
          label="Invoices"
          value={t?.invoiceCount}
          hint={t && `${fmtQty(t.qty, format)} units sold`}
          icon={<ReceiptLongRounded />}
          tone="info"
          loading={q.isLoading}
        />
        <StatTile
          label="Average sale"
          value={t && moneyCompact(t.invoiceCount ? num(t.total) / t.invoiceCount : 0, format)}
          hint="Per invoice"
          icon={<ShoppingCartOutlined />}
          tone="success"
          loading={q.isLoading}
        />
        <StatTile
          label="Best period"
          value={best && num(best.total) > 0 ? moneyCompact(best.total, format) : '—'}
          hint={
            best && num(best.total) > 0
              ? periodLabel(p.interval, best.periodStart, format.locale, true)
              : 'No sales yet'
          }
          icon={<ConfirmationNumberOutlined />}
          tone="warning"
          loading={q.isLoading}
        />
      </StatGrid>
      <Panel title="Sales trend" subtitle="Invoiced total per period" sx={{ mb: 2.5 }}>
        <TrendChart
          loading={q.isLoading}
          labels={(rows ?? []).map((r) => periodLabel(p.interval, r.periodStart, format.locale))}
          series={[
            {
              key: 'total',
              label: 'Total',
              data: (rows ?? []).map((r) => num(r.total)),
              brand: true,
              area: true,
            },
          ]}
        />
      </Panel>
      <ReportTable
        loading={q.isLoading}
        rows={rows ? rows.filter((r) => r.invoiceCount > 0).reverse() : undefined}
        rowKey={(r) => r.label}
        empty={emptyState}
        columns={[
          {
            key: 'period',
            header: 'Period',
            render: (r) => periodLabel(p.interval, r.periodStart, format.locale, true),
          },
          {
            key: 'count',
            header: 'Invoices',
            align: 'right',
            render: (r) => r.invoiceCount,
            footer: t?.invoiceCount,
          },
          {
            key: 'qty',
            header: 'Units',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <Q v={r.qty} />,
            footer: <Q v={t?.qty} />,
          },
          {
            key: 'net',
            header: 'Net',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => <M v={r.net} />,
            footer: <M v={t?.net} />,
          },
          {
            key: 'tax',
            header: 'Tax',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <M v={r.tax} />,
            footer: <M v={t?.tax} />,
          },
          {
            key: 'total',
            header: 'Total',
            align: 'right',
            render: (r) => <M v={r.total} strong />,
            footer: <M v={t?.total} />,
          },
        ]}
      />
    </ReportFrame>
  );
}

export function SalesByCustomerReport() {
  const p = useReportParams('thisMonth');
  const { format } = useShop();
  const query = { ...p.range, page: p.page, pageSize: p.pageSize };
  const q = useQuery({
    queryKey: ['reports', 'sales-by-customer', query],
    queryFn: () => unwrapJson(api.GET('/reports/sales/by-customer', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const t = q.data?.totals;
  const total = num(t?.total);
  return (
    <ReportFrame
      slug="sales-by-customer"
      period={usePeriodText(p)}
      controls={<RangeControls p={p} />}
      onExport={() =>
        fetchCsv(
          api.GET('/reports/sales/by-customer', {
            params: { query: { ...p.range, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName('sales-by-customer', p.range),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Total sales"
          value={t && moneyCompact(t.total, format)}
          icon={<TrendingUpRounded />}
          loading={q.isLoading}
        />
        <StatTile
          label="Customers"
          value={q.data?.page.total}
          hint="Bought in this period"
          icon={<PeopleAltOutlined />}
          tone="info"
          loading={q.isLoading}
        />
        <StatTile
          label="Invoices"
          value={t?.invoiceCount}
          icon={<ReceiptLongRounded />}
          tone="success"
          loading={q.isLoading}
        />
      </StatGrid>
      <Panel title="Top customers" subtitle="By invoiced total" sx={{ mb: 2.5 }}>
        <RankingBars
          loading={q.isLoading}
          items={(p.page === 1 ? (q.data?.data ?? []) : []).map((r) => ({
            label: r.name,
            value: num(r.total),
          }))}
        />
      </Panel>
      <ReportTable
        loading={q.isLoading}
        rows={q.data?.data}
        rowKey={(r) => r.contactId}
        empty={emptyState}
        page={q.data?.page}
        onPageChange={p.setPage}
        onPageSizeChange={p.setPageSize}
        expand={(r, subRow) => (
          <PartyDocumentRows side="sales" contactId={r.contactId} range={p.range} subRow={subRow} />
        )}
        columns={[
          { key: 'name', header: 'Customer', render: (r) => <ItemCell name={r.name} /> },
          {
            key: 'count',
            header: 'Invoices',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => r.invoiceCount,
            footer: t?.invoiceCount,
          },
          {
            key: 'net',
            header: 'Net',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <M v={r.net} />,
            footer: <M v={t?.net} />,
          },
          {
            key: 'tax',
            header: 'Tax',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <M v={r.tax} />,
            footer: <M v={t?.tax} />,
          },
          {
            key: 'total',
            header: 'Total',
            align: 'right',
            render: (r) => <M v={r.total} strong />,
            footer: <M v={t?.total} />,
          },
          {
            key: 'share',
            header: 'Share',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => share(num(r.total), total),
            footer: total ? '100%' : '—',
          },
        ]}
      />
    </ReportFrame>
  );
}

export function SalesByProductReport() {
  const p = useReportParams('thisMonth');
  const { format } = useShop();
  const groupBy = p.get('groupBy') === 'product' ? 'product' : 'variant';
  const query = { ...p.range, groupBy, page: p.page, pageSize: p.pageSize } as const;
  const q = useQuery({
    queryKey: ['reports', 'sales-by-product', query],
    queryFn: () => unwrapJson(api.GET('/reports/sales/by-product', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const t = q.data?.totals;
  const total = num(t?.total);
  return (
    <ReportFrame
      slug="sales-by-product"
      period={usePeriodText(p)}
      controls={
        <>
          <RangeControls p={p} />
          <Segmented
            label="Group by"
            value={groupBy}
            options={[
              { value: 'variant', label: 'Per variant' },
              { value: 'product', label: 'Per product' },
            ]}
            onChange={(v) => p.update({ groupBy: v === 'variant' ? null : v })}
          />
        </>
      }
      onExport={() =>
        fetchCsv(
          api.GET('/reports/sales/by-product', {
            params: { query: { ...p.range, groupBy, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName('sales-by-product', p.range),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Total sales"
          value={t && moneyCompact(t.total, format)}
          icon={<TrendingUpRounded />}
          loading={q.isLoading}
        />
        <StatTile
          label={groupBy === 'product' ? 'Products sold' : 'Variants sold'}
          value={q.data?.page.total}
          icon={<ShoppingCartOutlined />}
          tone="info"
          loading={q.isLoading}
        />
        <StatTile
          label="Tax collected"
          value={t && moneyCompact(t.tax, format)}
          icon={<ReceiptLongRounded />}
          tone="success"
          loading={q.isLoading}
        />
      </StatGrid>
      <Panel title="Top items" subtitle="By invoiced total" sx={{ mb: 2.5 }}>
        <RankingBars
          loading={q.isLoading}
          items={(p.page === 1 ? (q.data?.data ?? []) : []).map((r) => ({
            label: r.name,
            value: num(r.total),
          }))}
        />
      </Panel>
      <ReportTable
        loading={q.isLoading}
        rows={q.data?.data}
        rowKey={(r) => r.variantId ?? r.productId}
        empty={emptyState}
        page={q.data?.page}
        onPageChange={p.setPage}
        onPageSizeChange={p.setPageSize}
        columns={[
          { key: 'name', header: 'Item', render: (r) => <ItemCell name={r.name} sku={r.sku} /> },
          {
            key: 'qty',
            header: 'Qty',
            align: 'right',
            render: (r) => <Q v={r.qty} unit={r.unitSymbol} />,
          },
          {
            key: 'count',
            header: 'Invoices',
            align: 'right',
            hideBelow: 'md',
            render: (r) => r.invoiceCount,
          },
          {
            key: 'net',
            header: 'Net',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <M v={r.net} />,
            footer: <M v={t?.net} />,
          },
          {
            key: 'tax',
            header: 'Tax',
            align: 'right',
            hideBelow: 'lg',
            render: (r) => <M v={r.tax} />,
            footer: <M v={t?.tax} />,
          },
          {
            key: 'total',
            header: 'Total',
            align: 'right',
            render: (r) => <M v={r.total} strong />,
            footer: <M v={t?.total} />,
          },
          {
            key: 'share',
            header: 'Share',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => share(num(r.total), total),
          },
        ]}
      />
    </ReportFrame>
  );
}

export function BestSellersReport() {
  const p = useReportParams('30d');
  const by = p.get('by') === 'quantity' ? 'quantity' : 'revenue';
  const limit = [10, 20, 50].includes(Number(p.get('limit'))) ? Number(p.get('limit')) : 10;
  const query = { ...p.range, by, limit } as const;
  const q = useQuery({
    queryKey: ['reports', 'best-sellers', query],
    queryFn: () => unwrapJson(api.GET('/reports/best-sellers', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const rows = q.data?.data;
  const revenue = (rows ?? []).reduce((s, r) => s + num(r.revenue), 0);
  return (
    <ReportFrame
      slug="best-sellers"
      period={usePeriodText(p)}
      controls={
        <>
          <RangeControls p={p} />
          <Segmented
            label="Rank by"
            value={by}
            options={[
              { value: 'revenue', label: 'By revenue' },
              { value: 'quantity', label: 'By quantity' },
            ]}
            onChange={(v) => p.update({ by: v === 'revenue' ? null : v })}
          />
          <Segmented
            label="Show"
            value={String(limit)}
            options={[
              { value: '10', label: 'Top 10' },
              { value: '20', label: 'Top 20' },
              { value: '50', label: 'Top 50' },
            ]}
            onChange={(v) => p.update({ limit: v === '10' ? null : v })}
          />
        </>
      }
      onExport={() =>
        fetchCsv(
          api.GET('/reports/best-sellers', {
            params: { query: { ...query, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName('best-sellers', p.range),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <Panel
        title={`Top ${String(limit)} by ${by}`}
        subtitle="Best-selling variants"
        sx={{ mb: 2.5 }}
      >
        <RankingBars
          loading={q.isLoading}
          kind={by === 'revenue' ? 'money' : 'count'}
          label={by === 'revenue' ? 'Revenue' : 'Quantity'}
          max={10}
          items={(rows ?? []).map((r) => ({
            label: r.name,
            value: num(by === 'revenue' ? r.revenue : r.qty),
          }))}
        />
      </Panel>
      <ReportTable
        loading={q.isLoading}
        rows={rows}
        rowKey={(r) => r.variantId}
        empty={emptyState}
        columns={[
          {
            key: 'rank',
            header: '#',
            width: 56,
            render: (r) => (
              <Chip
                size="small"
                label={r.rank}
                color={r.rank <= 3 ? 'primary' : 'default'}
                variant={r.rank <= 3 ? 'filled' : 'outlined'}
                sx={{ minWidth: 32, fontWeight: 700 }}
              />
            ),
          },
          { key: 'name', header: 'Item', render: (r) => <ItemCell name={r.name} sku={r.sku} /> },
          {
            key: 'qty',
            header: 'Qty sold',
            align: 'right',
            render: (r) => <Q v={r.qty} unit={r.unitSymbol} />,
          },
          {
            key: 'count',
            header: 'Invoices',
            align: 'right',
            hideBelow: 'md',
            render: (r) => r.invoiceCount,
          },
          {
            key: 'revenue',
            header: 'Revenue',
            align: 'right',
            render: (r) => <M v={r.revenue} strong />,
            footer: <M v={revenue} />,
          },
          {
            key: 'share',
            header: 'Share',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => share(num(r.revenue), revenue),
          },
        ]}
        totalsLabel="Top total"
      />
    </ReportFrame>
  );
}
